import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const sessionTimeout = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  // Retrieve login state from localStorage
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const storedLoginState = localStorage.getItem("isLoggedIn");
    return storedLoginState === "true"; // Convert string to boolean
  });

  const logout = useCallback(() => {
    setIsLoggedIn(false);
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("sessionExpiry");
    navigate("/login");
  }, [navigate]);

  const resetSession = useCallback(() => {
    const expiryTime = Date.now() + sessionTimeout; // Set expiry time to 2 hours from now
    localStorage.setItem("sessionExpiry", expiryTime.toString()); // Store as a string
  }, [sessionTimeout]);

  const checkSession = useCallback(() => {
    const expiryTime = localStorage.getItem("sessionExpiry");
    if (expiryTime && Date.now() > parseInt(expiryTime, 10)) {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    if (isLoggedIn) {
      resetSession(); // Initialize session on login

      const interval = setInterval(() => {
        checkSession(); // Check session expiry every second
      }, 1000);

      return () => clearInterval(interval); // Cleanup interval on unmount
    }
  }, [isLoggedIn, checkSession, resetSession]);

  useEffect(() => {
    const handleActivity = () => {
      if (isLoggedIn) resetSession(); // Reset session on user activity
    };

    window.addEventListener("click", handleActivity);
    window.addEventListener("keypress", handleActivity);
    window.addEventListener("mousemove", handleActivity);

    return () => {
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keypress", handleActivity);
      window.removeEventListener("mousemove", handleActivity);
    };
  }, [isLoggedIn, resetSession]);

  // Save login state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("isLoggedIn", isLoggedIn.toString());
  }, [isLoggedIn]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, setIsLoggedIn, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => React.useContext(AuthContext);
